import {
  APEX_MAPPINGS,
  AUTONOMY_MAPPINGS,
  BALANCE_HEALTH_MAPPINGS,
  COUG_MAPPINGS,
  ENTP_MAPPINGS,
  MENT_MAPPINGS,
  HEARTPLACE_MAPPINGS,
  PACESETTER_MAPPINGS,
  PA_EAST_MAPPINGS,
  PINNACLE_MAPPINGS,
  PPCP_MAPPINGS,
  SIENT_MAPPINGS,
  STRIDECARE_MAPPINGS,
  TEXAS_HEART_SOUTH_HEART_MAPPINGS,
  TRICITY_MAPPINGS,
  USHV_CCOH_MAPPINGS,
  USHV_FORTWORTH_MAPPINGS,
  USHV_MCA_MAPPINGS,
  USHV_MKC_MAPPINGS,
  USHV_PIMA_MAPPINGS,
  USRC_CPS_MAPPINGS,
  VPS_MAPPINGS,
} from "@/configuration/document-mappings";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import { PINNACLE_PRACTITIONER_MAPPINGS } from "@/configuration/groups/pinnacle/generated/practitioner-mappings";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { getTransformConfig } from "@/configuration/transform-config";
import type { FieldTransformConfig, LabAbbreviation } from "@/configuration/transform-config";
export type { FieldTransformConfig, LabAbbreviation } from "@/configuration/transform-config";
import { logger } from "@/src/lib/logging/logger";
import { formatDateWithDots, formatDateWithSpec } from "@/src/lib/utils/date-and-phone-utils";
import { Group } from "@/src/models/group";
import { getPersonName } from "@/src/models/person";
import { Practitioner, getProviderNameWithCredentials } from "@/src/models/practitioner";

// Options for value processing
interface ProcessValueOptions {
  /** Whether to treat string values as potential dates */
  parseDates?: boolean;
}

// Default processing options
const DEFAULT_OPTIONS: ProcessValueOptions = {
  parseDates: false,
};

/**
 * Attempts to parse a string as a date and format it
 */
function tryParseAndFormatDate(value: string): string | null {
  const date = new Date(value);

  return !isNaN(date.getTime()) ? formatDateWithDots(date) : null;
}

/**
 * Extracts a display value from an object using common property names
 */
function extractObjectDisplayValue(obj: Record<string, unknown>): string {
  const displayProperties = ["displayName", "name", "label", "title", "text", "value"];
  const foundProp = displayProperties.find((prop) => prop in obj && obj[prop] != null);

  return foundProp ? String(obj[foundProp]) : "";
}

/**
 * Handles array values by recursively processing each item
 */
function processArrayValue(value: unknown[], processor: (val: unknown) => string): string {
  return value.map((item) => processor(item)).join(", ");
}

/**
 * Main value processing function that handles all types consistently
 */
export function processValue(value: unknown, options: ProcessValueOptions = DEFAULT_OPTIONS): string {
  // Handle null/undefined
  if (value == null) return "";

  // Handle arrays
  if (Array.isArray(value)) {
    return processArrayValue(value, (item) => processValue(item, options));
  }

  // Handle Date objects
  if (value instanceof Date) {
    return formatDateWithDots(value);
  }

  // Handle objects (but not Date objects)
  if (typeof value === "object" && value !== null) {
    const displayValue = extractObjectDisplayValue(value as Record<string, unknown>);

    if (displayValue) {
      return displayValue;
    }

    // Handle single-property objects
    const keys = Object.keys(value);

    if (keys.length === 1) {
      return processValue((value as Record<string, unknown>)[keys[0]], options);
    }

    // Fallback to JSON string
    return JSON.stringify(value);
  }

  // Handle string dates
  if (typeof value === "string" && options.parseDates) {
    const formattedDate = tryParseAndFormatDate(value);

    if (formattedDate) {
      return formattedDate;
    }
  }

  // Handle all other types
  return String(value);
}

function abbreviateLabName(name: string): string {
  const words = name.trim().split(/\s+/);

  if (words.length < 2) return name.trim();

  return words.map((w) => w[0].toUpperCase()).join("");
}

function normalizeLabName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function applyCanonicalAbbreviations(
  list: string[],
  abbreviations: readonly LabAbbreviation[],
  priorityAbbreviations: readonly string[],
  maxRange: number,
  maxLength: number
): string {
  const aliasLookup = new Map<string, string>();

  for (const entry of abbreviations) {
    for (const alias of entry.aliases) {
      aliasLookup.set(normalizeLabName(alias), entry.canonical);
    }
  }

  const mapped = list.map((name) => aliasLookup.get(normalizeLabName(name)) ?? name);

  const priorityItems: string[] = [];

  for (const priority of priorityAbbreviations) {
    for (const item of mapped) {
      if (item === priority) priorityItems.push(item);
    }
  }

  const prioritySet = new Set<string>(priorityAbbreviations);
  const remainingItems = mapped.filter((item) => !prioritySet.has(item));

  const selected = [...priorityItems, ...remainingItems].slice(0, maxRange);

  let joined = selected.join(", ");

  while (joined.length > maxLength && selected.length > 0) {
    selected.pop();
    joined = selected.join(", ");
  }

  return joined;
}

function isLikelyDateFieldName(fieldName: string): boolean {
  return fieldName.toLowerCase().includes("date");
}

/**
 * Checks if a field name or value is date-related
 */
function isLikelyDateField(fieldName: string, value: unknown): boolean {
  return (
    isLikelyDateFieldName(fieldName) ||
    value instanceof Date ||
    (typeof value === "string" && !isNaN(Date.parse(value)))
  );
}

/**
 * Applies custom transformations based on field name and configuration
 */
function applyCustomTransformations(
  key: string,
  value: unknown,
  config: FieldTransformConfig,
  data: Record<string, unknown>
): string | null {
  const resolveProvider = () =>
    (data.provider ?? data.orderingProvider ?? data.receivingProvider) as Practitioner | null | undefined;

  const transformers = {
    signatureRequired: (val: unknown) => {
      if (!config.signatureRequired) return "";

      return val ? config.signatureRequired.trueValue : config.signatureRequired.falseValue;
    },
    vaccineType: (val: unknown) => {
      if (!config.vaccineType) return null;

      if (val === "" || val == null) return config.vaccineType.emptyValue;

      return processValue(val, { parseDates: false }) + config.vaccineType.suffix;
    },
    supplyDuration: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    clinicName: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    providerLastName: (val: unknown) => (data.orderingProvider as Practitioner)?.familyName || String(val),
    provider: (val: unknown) => {
      const p = resolveProvider();

      return p != null ? getProviderNameWithCredentials(p) : String(val);
    },
    providerName: (val: unknown) => {
      const p = resolveProvider();

      if (p != null) return getPersonName(p, false);
      if (val === null || val === undefined) return null;

      return String(val);
    },
    referringProvider: (val: unknown) => (val ? getProviderNameWithCredentials(val as Practitioner) : String(val)),
    isSigned: (val: unknown) => {
      if (!config.isSigned) return null;

      return val ? config.isSigned.trueValue : config.isSigned.falseValue;
    },
    significantFindings: (val: unknown) => {
      if (!config.significantFindings) return "";

      return val ? config.significantFindings.trueValue : config.significantFindings.falseValue;
    },
    bodyPartLaterality: (val: unknown) => {
      if (val === null || val === undefined || val === "") return "";

      const canonical = String(val)
        .toLowerCase()
        .replace(/[\s._-]/g, "");

      if (canonical === "other") return "";

      return `${String(val)} `;
    },
    orderNumber: (val: unknown) => {
      if (!config.orderNumber) return "";

      if (val === null || val === undefined) return config.orderNumber.emptyValue;

      return String(val);
    },
    specialty: (val: unknown) => {
      if (!config.specialty) return "";

      if (val === null || val === undefined) return config.specialty.emptyValue;

      return String(val);
    },
    listOfNameOfLabs: (val: unknown) => {
      if (!config.listOfNameOfLabs) return null;

      if (val === null || val === undefined) return null;

      const list = (Array.isArray(val) ? val : [val]).map(String);
      const { maxLength, maxRange, abbreviations, priorityAbbreviations } = config.listOfNameOfLabs;

      if (abbreviations && priorityAbbreviations) {
        return applyCanonicalAbbreviations(list, abbreviations, priorityAbbreviations, maxRange, maxLength);
      }

      const joined = list.join(", ");

      if (joined.length <= maxLength) return joined;

      const abbreviated = list.map(abbreviateLabName);
      const abbreviatedJoined = abbreviated.join(", ");

      if (abbreviatedJoined.length <= maxLength) return abbreviatedJoined;

      return abbreviated
        .toSorted((a, b) => a.length - b.length)
        .slice(0, maxRange)
        .join(", ");
    },
    labTypesAndResults: (val: unknown) => {
      if (!config.labTypesAndResults) return null;

      if (val === null || val === undefined) return null;

      const list = Array.isArray(val) ? val : [val];

      // Extract typeOfLab from each object and filter out null/undefined values
      const typeOfLabs = list
        .map((item) => {
          if (typeof item === "object" && item !== null && "typeOfLab" in item) {
            return item.typeOfLab;
          }

          return null;
        })
        .filter((type): type is string => type !== null && type !== undefined);

      if (typeOfLabs.length === 0) return null;

      if (typeOfLabs.length > config.labTypesAndResults.maxRange) {
        return typeOfLabs.slice(0, config.labTypesAndResults.maxRange).join(", ");
      }

      return typeOfLabs.join(", ");
    },
  };

  const transformer = transformers[key as keyof typeof transformers];

  return transformer ? transformer(value) : null;
}

/**
 * Renders a template string by replacing placeholders with values from data
 */
export function renderTemplate(
  template: string | null | undefined,
  data: Record<string, unknown>,
  group: Pick<Group, "subdomain">,
  options?: { config?: FieldTransformConfig; stripUnresolved?: boolean }
): string {
  if (!template) {
    return "";
  }

  const effectiveConfig = options?.config ?? getTransformConfig(group);

  const rendered = template
    .replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      const [fieldName, formatSpec] = key.trim().split(/\s+/);
      const value = data[fieldName];

      const customResult = applyCustomTransformations(fieldName, value, effectiveConfig, data);

      if (customResult !== null) return customResult;

      if (value === undefined || value === null) {
        return match;
      }

      if (formatSpec && isLikelyDateField(fieldName, value)) {
        return formatDateWithSpec(value, formatSpec);
      }

      return processValue(value, { parseDates: isLikelyDateFieldName(fieldName) });
    })
    .trim();

  if (options?.stripUnresolved) {
    return rendered
      .replace(/\{\{[^}]*\}\}/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  return rendered;
}

const GROUPS_WITH_MAPPINGS_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.APEX,
  ConfigSubdomain.AUTONOMY,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.MASS_ENT,
  ConfigSubdomain.PINNACLE,
  ConfigSubdomain.HEARTPLACE,
  ConfigSubdomain.VALERIE,
  ConfigSubdomain.VALERIE_HEALTH_TRAINING,
  ConfigSubdomain.COUG,
  ConfigSubdomain.BALANCE_HEALTH,
  ConfigSubdomain.STRIDECARE,
  ConfigSubdomain.USHV_CCOH,
  ConfigSubdomain.USHV_FORTWORTH,
  ConfigSubdomain.USHV_MCA,
  ConfigSubdomain.USHV_MKC,
  ConfigSubdomain.TEXAS_HEART_SOUTH_HEART,
  ConfigSubdomain.TRICITY,
  ConfigSubdomain.VPS,
  ConfigSubdomain.PA_EAST_REGION,
  ConfigSubdomain.SIENT,
  ConfigSubdomain.PACESETTER,
  ConfigSubdomain.PPCP,
]);

export function hasOwnMappings(group: Group): boolean {
  return GROUPS_WITH_MAPPINGS_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

/**
 * Get the appropriate mappings based on group name.
 * Returns group-specific mappings if available, otherwise falls back to Pinnacle mappings.
 */
export function getMappings(group: Group) {
  switch (getGroupConfigSubdomain(group)) {
    case ConfigSubdomain.APEX:
      return APEX_MAPPINGS;
    case ConfigSubdomain.AUTONOMY:
      return AUTONOMY_MAPPINGS;
    case ConfigSubdomain.ENT_PARTNERS:
      return ENTP_MAPPINGS;
    case ConfigSubdomain.ENT_PARTNERS_MENT:
      return MENT_MAPPINGS;
    case ConfigSubdomain.PINNACLE:
      return PINNACLE_MAPPINGS;
    case ConfigSubdomain.HEARTPLACE:
      return HEARTPLACE_MAPPINGS;
    case ConfigSubdomain.VALERIE:
      return PINNACLE_MAPPINGS; // Valerie uses Pinnacle mappings for now
    case ConfigSubdomain.VALERIE_HEALTH_TRAINING:
      return HEARTPLACE_MAPPINGS;
    case ConfigSubdomain.COUG:
      return COUG_MAPPINGS;
    case ConfigSubdomain.BALANCE_HEALTH:
      return BALANCE_HEALTH_MAPPINGS;
    case ConfigSubdomain.STRIDECARE:
      return STRIDECARE_MAPPINGS;
    case ConfigSubdomain.USHV_CCOH:
      return USHV_CCOH_MAPPINGS;
    case ConfigSubdomain.USHV_FORTWORTH:
      return USHV_FORTWORTH_MAPPINGS;
    case ConfigSubdomain.USHV_MCA:
      return USHV_MCA_MAPPINGS;
    case ConfigSubdomain.USHV_MKC:
      return USHV_MKC_MAPPINGS;
    case ConfigSubdomain.USHV_PIMA:
      return USHV_PIMA_MAPPINGS;
    case ConfigSubdomain.USRC_CPS_AK:
    case ConfigSubdomain.USRC_CPS_CO:
      return USRC_CPS_MAPPINGS;
    case ConfigSubdomain.TEXAS_HEART_SOUTH_HEART:
      return TEXAS_HEART_SOUTH_HEART_MAPPINGS;
    case ConfigSubdomain.TRICITY:
      return TRICITY_MAPPINGS;
    case ConfigSubdomain.VPS:
      return VPS_MAPPINGS;
    case ConfigSubdomain.PA_EAST_REGION:
      return PA_EAST_MAPPINGS;
    case ConfigSubdomain.SIENT:
      return SIENT_MAPPINGS;
    case ConfigSubdomain.PACESETTER:
      return PACESETTER_MAPPINGS;
    case ConfigSubdomain.PPCP:
      return PPCP_MAPPINGS;
    default:
      logger.warn("No document mappings registered for group, returning empty registry", {
        subdomain: getGroupConfigSubdomain(group),
      });

      return {};
  }
}

/**
 * Get the appropriate practitioner mappings based on group name
 * @param group - The group to get the practitioner mappings for
 * @returns The appropriate practitioner mappings based on group name
 */
export function getPractitionerMappings(group: Group) {
  switch (getGroupConfigSubdomain(group)) {
    case ConfigSubdomain.AUTONOMY:
      return AUTONOMY_PRACTITIONER_MAPPINGS;
    case ConfigSubdomain.PINNACLE:
      return PINNACLE_PRACTITIONER_MAPPINGS;
    default:
      return PINNACLE_PRACTITIONER_MAPPINGS;
  }
}
